import { useCallback } from "react";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { usePreferences } from "@/hooks/firestore/user/usePreferences";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { submitBet } from "@/features/betslip/services/submitBet";
import type { OddsPreference } from "@/features/betslip/types";

type SubmitOptions = {
  /** Skip the persisted last-submitted duplicate guard. */
  acceptDuplicate?: boolean;
};

/**
 * Wraps the side-effects every "place a bet" caller would otherwise
 * duplicate: pulling auth, preferences, profile, then handing off to
 * the submission service.
 *
 * `MANUAL` is the default odds preference while prefs are still
 * loading so we never silently auto-accept odds drift on the first
 * render.
 */
export const useSubmitBet = (): ((options?: SubmitOptions) => void) => {
  const userId = useUserId();
  const fiatCurrency = useFiatCurrency();
  const { preferences } = usePreferences();
  const { profile } = useProfile();

  const oddsPreference: OddsPreference =
    (preferences.acceptOdds as OddsPreference | null) ?? "MANUAL";

  // `profile` is whole-object identity; depend on the primitive fields
  // the service actually reads so the callback reference is stable when
  // the user's name/email haven't changed.
  return useCallback(
    (options?: SubmitOptions) => {
      if (!profile) return;
      submitBet({
        store: useBetslipStore,
        currency: fiatCurrency,
        preference: oddsPreference,
        userId,
        profile,
        acceptDuplicate: options?.acceptDuplicate,
      });
    },
    [
      profile?.email,
      profile?.firstName,
      profile?.lastName,
      fiatCurrency,
      oddsPreference,
      userId,
    ],
  );
};
